// Imports
import AOS from 'aos';
import Lenis from 'lenis';
import { gsap } from 'gsap';
import { SplitText } from 'gsap/SplitText';
import '@tailwindplus/elements';

/* Custom scripts */
import './popup';
import './kennisbank-filter';
import './kennisbank-toc';

gsap.registerPlugin(SplitText);

// Initialize Lenis smooth scrolling (desktop only)
const isDesktop = window.matchMedia('(pointer: fine) and (hover: hover)').matches;
const lenis = isDesktop ? new Lenis({
    duration: 0.9,
    wheelMultiplier: 1.1,
    smoothWheel: true,
}) : null;

if (lenis) {
    function raf(time) {
        lenis.raf(time);
        requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);
}

// Export lenis for other scripts (e.g., ScrollTrigger integration)
window.lenis = lenis;

// Handle anchor links with Lenis smooth scroll
document.addEventListener('click', (e) => {
    const anchor = e.target.closest('a[href^="#"]');
    if (anchor) {
        const target = anchor.getAttribute('href');
        if (target && target !== '#') {
            e.preventDefault();
            if (lenis) {
                lenis.scrollTo(target, { offset: -125, duration: 1.2 });
            } else {
                document.querySelector(target)?.scrollIntoView({ behavior: 'smooth' });
            }
        }
    }
});

// Scroll to anchor on page load (for cross-page anchor links like /informatie#parkeren)
if (window.location.hash) {
    const target = document.querySelector(window.location.hash);
    if (target) {
        // Small delay to let Lenis initialize and page render
        requestAnimationFrame(() => {
            if (lenis) {
                lenis.scrollTo(target, { offset: -125, duration: 1.2, immediate: false });
            } else {
                target.scrollIntoView({ behavior: 'smooth' });
            }
        });
    }
}

// Taxonomy informatie_categorie page animation
(function () {
    const title = document.querySelector('.taxonomy-informatie_categorie-title, .single-schoolgids-title, .archive-schoolgids-title');
    const crosses = document.querySelectorAll('.taxonomy-cross');
    const items = document.querySelectorAll('.menu-grid-item');
    const searchForm = document.querySelector('.search-form');

    if (!title && !crosses.length && !items.length && !searchForm) return;

    const tl = gsap.timeline({ delay: 0.1 });

    if (title) {
        const split = new SplitText(title, { type: 'words' });
        gsap.set(title, { autoAlpha: 1 });
        gsap.set(split.words, { autoAlpha: 0 });
        tl.fromTo(split.words,
            { y: 40, autoAlpha: 0 },
            { y: 0, autoAlpha: 1, duration: 0.7, stagger: 0.06, ease: 'power3.out' }
        );
    }

    if (crosses.length) {
        tl.fromTo(crosses,
            { y: 25, scale: 0.75, autoAlpha: 0 },
            { y: 0, scale: 1, autoAlpha: 1, duration: 0.6, stagger: 0.1, ease: 'power2.out' },
            '<=0.1'
        );
    }

    if (items.length) {
        tl.fromTo(items,
            { y: 25, autoAlpha: 0 },
            { y: 0, autoAlpha: 1, duration: 0.5, stagger: 0.06, ease: 'power2.out' },
            '<'
        );
    }

    if (searchForm) {
        tl.fromTo(searchForm,
            { y: 25, autoAlpha: 0 },
            { y: 0, autoAlpha: 1, duration: 0.5, stagger: 0.06, ease: 'power2.out' },
            '<=0.5'
        );
    }
})();

// Mobile menu (native <dialog>)
(function () {
    const dialog = document.getElementById('mobile-menu-dialog');
    if (!dialog) return;

    // Open
    document.querySelectorAll('#mobile-menu-open, #mobile-menu-open-2').forEach(btn => {
        btn.addEventListener('click', () => dialog.showModal());
    });

    // Close via button
    document.getElementById('mobile-menu-close')?.addEventListener('click', () => dialog.close());

    // Close when clicking backdrop (the ::backdrop pseudo-element)
    dialog.addEventListener('click', (e) => {
        if (e.target === dialog) dialog.close();
    });

    // Sub-menu toggles
    dialog.querySelectorAll('.mobile-sub-toggle').forEach(btn => {
        btn.addEventListener('click', () => {
            const sub = document.getElementById(btn.dataset.target);
            if (!sub) return;
            const open = !sub.hidden;
            sub.hidden = open;
            btn.setAttribute('aria-expanded', !open);
            btn.querySelector('svg').style.transform = open ? '' : 'rotate(180deg)';
        });
    });
})();

// Nav dropdown backdrop
document.addEventListener('DOMContentLoaded', function () {
    const backdrop = document.getElementById('nav-backdrop');
    const dropdowns = document.querySelectorAll('header .group');

    if (backdrop && dropdowns.length) {
        dropdowns.forEach((el) => {
            el.addEventListener('mouseenter', () => {
                backdrop.classList.remove('opacity-0', 'invisible');
                backdrop.classList.add('opacity-100', 'visible');
            });
            el.addEventListener('mouseleave', () => {
                backdrop.classList.add('opacity-0', 'invisible');
                backdrop.classList.remove('opacity-100', 'visible');
            });
        });
    }
});

// Search pagination: scroll to #booom-search on paginated page load
(function () {
    const searchSection = document.getElementById('booom-search');
    if (!searchSection) return;

    // Append hash to pagination links so the URL carries it after navigation
    document.querySelectorAll('nav[aria-label="Pagination"] a').forEach((link) => {
        const url = new URL(link.href);
        url.hash = 'booom-search';
        link.href = url.toString();
    });

    // Scroll to section when arriving via pagination (hash present in URL)
    if (window.location.hash === '#booom-search') {
        history.replaceState(null, '', window.location.pathname + window.location.search);
        if (lenis) {
            lenis.scrollTo(searchSection, { offset: -125, immediate: true });
        } else {
            searchSection.scrollIntoView();
        }
    }
})();

// Initialize AOS
document.addEventListener('DOMContentLoaded', function () {
    AOS.init({
        duration: 1200,
        easing: 'expo-out',
        offset: 50,
        once: true,
        anchorPlacement: 'top-bottom',
    });

    // Force elements with .aos-onload to animate immediately on page load
    setTimeout(() => {
        document.querySelectorAll('.aos-onload[data-aos]').forEach((el) => {
            el.classList.add('aos-animate');
        });
    }, 100);
});

document.addEventListener('DOMContentLoaded', function () {
    var btn = document.getElementById('back-to-top');
    if (!btn) return;

    function update() {
        var visible = window.scrollY > window.innerHeight * 0.5;
        btn.classList.toggle('opacity-0', !visible);
        btn.classList.toggle('pointer-events-none', !visible);
        btn.classList.toggle('translate-y-2', !visible);
    }

    window.addEventListener('scroll', update, { passive: true });

    btn.addEventListener('click', () => {
        if (lenis) {
            lenis.scrollTo(0, { duration: 1.2 });
        } else {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    });
});
document.addEventListener('DOMContentLoaded', function () {
    var submitBtn = document.getElementById('newsletter-submit');
    if (!submitBtn) return;

    var ICON_CHECK = '<path fill-rule="evenodd" clip-rule="evenodd" d="M13.707 3.293a1 1 0 0 1 0 1.414l-7 7a1 1 0 0 1-1.414 0l-3-3a1 1 0 1 1 1.414-1.414L6 9.586l6.293-6.293a1 1 0 0 1 1.414 0z" fill="currentColor"/>';
    var ICON_ERROR = '<path fill-rule="evenodd" clip-rule="evenodd" d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1zM7 5a1 1 0 1 1 2 0v3a1 1 0 1 1-2 0V5zm1 6a1 1 0 1 0 0 2 1 1 0 0 0 0-2z" fill="currentColor"/>';

    function showMessage(text, type) {
        var message = document.getElementById('newsletter-message');
        var icon    = document.getElementById('newsletter-message-icon');
        var label   = document.getElementById('newsletter-message-text');

        message.classList.remove('hidden', 'success', 'error');
        message.classList.add(type);
        icon.innerHTML = type === 'success' ? ICON_CHECK : ICON_ERROR;
        label.textContent = text;
    }

    submitBtn.addEventListener('click', function () {
        var input = document.getElementById('newsletter-email');
        var email = input ? input.value.trim() : '';

        if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            showMessage('Vul een geldig e-mailadres in.', 'error');
            return;
        }

        submitBtn.disabled = true;
        submitBtn.classList.add('opacity-60', 'cursor-not-allowed');

        var data = new FormData();
        data.append('action', 'booom_newsletter');
        data.append('nonce', window.booomNewsletter.nonce);
        data.append('email', email);

        fetch(window.booomNewsletter.ajaxUrl, { method: 'POST', body: data })
            .then(function (r) { return r.json(); })
            .then(function (res) {
                if (res.success) {
                    input.value = '';
                    document.getElementById('newsletter-form').classList.add('hidden');
                    showMessage(res.data.message, 'success');
                } else {
                    showMessage(res.data.message, 'error');
                    submitBtn.disabled = false;
                    submitBtn.classList.remove('opacity-60', 'cursor-not-allowed');
                }
            })
            .catch(function () {
                showMessage('Er is iets misgegaan. Probeer het opnieuw.', 'error');
                submitBtn.disabled = false;
                submitBtn.classList.remove('opacity-60', 'cursor-not-allowed');
            });
    });
});
